/* eslint-disable import/prefer-default-export */
import { DOMParser, XMLSerializer } from './dom';

function serialize(doc) {
  // Try to serialize the result. This should work, but malformed input could
  // result in an error. If serialization fails, return the original input.
  const serializer = new XMLSerializer();
  let serialized;

  try {
    serialized = serializer
      .serializeToString(doc)
      // XMLSerializer adds a NS, but we don't want it. note the leading space.
      .replace(' xmlns="http://www.w3.org/1999/xhtml"', '')
      // the html/head/body tags are added by the browser implementation
      .replace(/^<html><head><\/head><body>\s*/gi, '')
      .replace(/\s*<\/body>\s*<\/html>$/gi, '');
  } catch (e) {
    // CLEANUP: better handling/logging of errors
    console.error(e.toString()); // eslint-disable-line
  }
  return serialized;
}

/**
 * Replace tags
 *
 * @export
 * @param {String} html html source
 * @param {Array} tags tags to replace
 * @param {String} replacement replacement type, e.g. span
 * @returns {String} updated html
 */
export function replaceTagsInHtml(html, tags, replacement = 'span') {
  const parser = new DOMParser();
  // Wrap in div so there is always a parent node
  // TEST THIS
  const doc = parser.parseFromString(
    `<div id="dom">${html}</div>`,
    'text/html'
  );
  // Iterate over each tag, replacing any matches in the doc
  tags.forEach(tag => {
    const els = doc.getElementsByTagName(tag.toLowerCase());
    Array.from(els).forEach(el => {
      const rep = doc.createElement(replacement);
      // Add child nodes to new element
      Array.from(el.childNodes).forEach(child => {
        rep.appendChild(child);
      });
      el.parentNode.replaceChild(rep, el);
    });
  });

  const serialized = serialize(doc);
  return serialized
    ? serialized
        // Remove wrapper
        .replace(/^<div id="dom">\s*/gi, '')
        .replace(/\s*<\/div>$/gi, '')
    : html;
}

/**
 * Remove blacklisted tags
 *
 * @export
 * @param {String} html html source
 * @param {Array} tags tags to remove
 * @returns {String} updated html
 */
export function removeTagsInHtml(html, tags = []) {
  const parser = new DOMParser();
  // Wrap in div so `<blacklisted></blacklisted>` returns "" instead of an error
  // during serialization.
  const doc = parser.parseFromString(
    `<div id="dom">${html}</div>`,
    'text/html'
  );
  // Iterate over each blacklisted tag removing any matches from the doc
  tags.forEach(tag => {
    const els = doc.getElementsByTagName(tag.toLowerCase());
    Array.from(els).forEach(el => {
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
  });
  const serialized = serialize(doc);
  return serialized
    ? serialized
        // Remove wrapper
        .replace(/^<div id="dom">\s*/gi, '')
        .replace(/\s*<\/div>$/gi, '')
    : html;
}

/**
 * find all tags in the provided HTML and return a new HTML string that
 * contains updated tags (attrs + contents) after running through the passed
 * tag processing functions.
 *
 * @export
 * @param {String} [html=''] html source
 * @param {String} [tagName='a'] tag name
 * @param {Function[]} [tagProcessors=[]]
 * @returns {String} updated html
 */
export function updateTagsInHtml(html = '', tagName = 'a', tagProcessors = []) {
  const parser = new DOMParser();

  // CLEANUP: do we need to catch warnings here? is it even possible?
  const doc = parser.parseFromString(html, 'text/html');

  if (!doc) {
    // Unable to parse HTML string
    return html;
  }

  const tags = doc.getElementsByTagName(tagName.toLowerCase());
  // allow a single function to be passed in without being wrapped in an array
  const tagProcessorFns = Array.isArray(tagProcessors)
    ? tagProcessors
    : [tagProcessors];

  // apply all the tag processors one after another
  tagProcessorFns.forEach(fn => {
    // update the tags in-place on doc
    //
    // NOTE: using Array.from() here instead of spread because xmldom doesn't
    // implement an iterator.
    Array.from(tags).forEach(fn);
  });

  return serialize(doc) || html;
}

/**
 * adds rel="nofollow" to provided tag if it's a link.
 *
 * @export
 * @param {HTMLElement} tag
 */
export function addNofollowToLink(tag) {
  if (tag.tagName.toLowerCase() === 'a') {
    tag.setAttribute('rel', 'nofollow');
  }
}

/**
 * Decodes HTML entities
 *
 * @export
 * @param {String} str
 */
export function decodeEntities(str) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<textarea id="textarea">${str}</textarea>`,
    'text/html'
  );
  return doc.getElementById('textarea').textContent;
}
